import {createShortcut, defineInputSystem} from '@wandb/components';

/*
Shortcut migration checklist:

app:
  core/frontends/app/src/components/MultiRunWorkspace/MultiRunWorkspaceContentContainer.tsx (global_document_keydown, own_listener=yes)
    - [x] Cmd+.: toggle minimized run selector
    - [x] Cmd+J: expand or collapse runs sidebar/table
    - [x] Ctrl+.: toggle minimized run selector
    - [x] Ctrl+J: expand or collapse runs sidebar/table
  core/frontends/app/src/components/Panel/PanelEditor/PanelEditor.tsx (global_document_keydown, own_listener=yes)
    - [ ] Cmd+Enter: submit panel editor
    - [ ] Ctrl+Enter: submit panel editor
  core/frontends/app/src/components/Panel/useNextAndPreviousPanel.ts (global_window_keydown, own_listener=yes)
    - [ ] ArrowLeft: go to previous panel
    - [ ] ArrowRight: go to next panel
  core/frontends/app/src/components/PanelMediaBrowser/MediaBrowserPanelInner.tsx (helper_binding_map, own_listener=no)
    - [ ] Control+ArrowLeft: define full-screen step-slider decrement binding on non-macOS
    - [ ] Control+ArrowRight: define full-screen step-slider increment binding on non-macOS
    - [ ] Meta+ArrowLeft: define full-screen step-slider decrement binding
    - [ ] Meta+ArrowRight: define full-screen step-slider increment binding
  core/frontends/app/src/components/PanelMediaBrowser/controls/GenericSliderDropdown.tsx (configurable_global_document_keydown_capture, own_listener=yes)
    - [ ] <configured decrement key>: decrement slider dropdown value by configured stride
    - [ ] <configured increment key>: increment slider dropdown value by configured stride
  core/frontends/app/src/components/PanelMediaBrowser/controls/Slider.tsx (configurable_global_document_keydown_capture, own_listener=yes)
    - [ ] <configured decrement key>: decrement slider value via configured binding
    - [ ] <configured increment key>: increment slider value via configured binding
  core/frontends/app/src/components/PanelMediaBrowser/layout/utils/slotUtils.ts (helper_binding_map, own_listener=no)
    - [ ] Alt+ArrowLeft: define full-screen index-slider decrement binding
    - [ ] Alt+ArrowRight: define full-screen index-slider increment binding
    - [ ] Control+ArrowLeft: define full-screen step-slider decrement binding on non-macOS
    - [ ] Control+ArrowRight: define full-screen step-slider increment binding on non-macOS
    - [ ] Meta+ArrowLeft: define full-screen step-slider decrement binding
    - [ ] Meta+ArrowRight: define full-screen step-slider increment binding
  core/frontends/app/src/components/PanelMediaBrowser/modal/MediaBrowserFullscreenModal.tsx (global_delegated_helper, own_listener=yes)
    - [x] !: zoom modal media to fit (Shift+Digit1 via Plus alias)
    - [ ] Alt+ArrowLeft: decrement media index slider
    - [ ] Alt+ArrowRight: advance media index slider
    - [ ] ArrowLeft: select previous run in modal
    - [ ] ArrowRight: select next run in modal
    - [x] Cmd++: zoom in modal media (Primary+Plus)
    - [x] Cmd+-: zoom out modal media (Primary+Minus)
    - [x] Cmd+0: reset modal zoom to 100 percent (Primary+Digit0)
    - [x] Cmd+=: zoom in modal media (Primary+Plus)
    - [ ] Cmd+ArrowLeft: decrement media step slider
    - [ ] Cmd+ArrowRight: advance media step slider
    - [x] Ctrl++: zoom in modal media (Primary+Plus)
    - [x] Ctrl+-: zoom out modal media (Primary+Minus)
    - [x] Ctrl+0: reset modal zoom to 100 percent (Primary+Digit0)
    - [x] Ctrl+=: zoom in modal media (Primary+Plus)
    - [ ] Ctrl+ArrowLeft: decrement media step slider
    - [ ] Ctrl+ArrowRight: advance media step slider
    - [x] Escape: close media browser modal (automatic in cmp lib)
    - [x] Shift+1: zoom modal media to fit (Shift+Digit1)
  core/frontends/app/src/components/Panel/FullScreenPanel/FullScreenPanelHeader.tsx (global_document_keydown, own_listener=yes)
    - [x] Escape: navigate back from full-screen panel unless drawer is open  (automatic in cmp lib)
  core/frontends/app/src/components/PanelMediaBrowser/modal/useModalKeyboardHandling.ts (delegated_helper, own_listener=no)
    - [x] !: zoom modal media to fit (Shift+Digit1 via Plus alias)
    - [ ] Alt+ArrowLeft: decrement media index slider
    - [ ] Alt+ArrowRight: advance media index slider
    - [ ] ArrowLeft: select previous run in modal
    - [ ] ArrowRight: select next run in modal
    - [x] Cmd++: zoom in modal media (Primary+Plus)
    - [x] Cmd+-: zoom out modal media (Primary+Minus)
    - [x] Cmd+0: reset modal zoom to 100 percent (Primary+Digit0)
    - [x] Cmd+=: zoom in modal media (Primary+Plus)
    - [ ] Cmd+ArrowLeft: decrement media step slider
    - [ ] Cmd+ArrowRight: advance media step slider
    - [x] Ctrl++: zoom in modal media (Primary+Plus)
    - [x] Ctrl+-: zoom out modal media (Primary+Minus)
    - [x] Ctrl+0: reset modal zoom to 100 percent (Primary+Digit0)
    - [x] Ctrl+=: zoom in modal media (Primary+Plus)
    - [ ] Ctrl+ArrowLeft: decrement media step slider
    - [ ] Ctrl+ArrowRight: advance media step slider
    - [x] Escape: close media browser modal (automatic in cmp lib)
    - [x] Shift+1: zoom modal media to fit (Shift+Digit1)
  core/frontends/app/src/components/RunSelectAll.tsx (global_document_keydown, own_listener=yes)
    - [x] Alt+V: toggle only-show-visualized runs
    - [x] Mac Option+V (event.key=√): toggle only-show-visualized runs (now via physical code KeyV + Alt)
  core/frontends/app/src/components/Search/keyboard.ts (global_document_keydown, own_listener=yes)
    - [x] Cmd+K: toggle app search
    - [x] Ctrl+K: toggle app search
    - [x] Escape: close app search (automatic in cmp lib)
  core/frontends/app/src/components/Search/keyboard.ts (local_onKeyDown, own_listener=yes)
    - [ ] ArrowDown: move app-search selection down
    - [ ] ArrowUp: move app-search selection up
    - [ ] Enter: open selected search result or direct-navigate slash query
  core/frontends/app/src/components/Vega3/QueryEditor.tsx (global_document_keydown, own_listener=yes)
    - [x] Cmd+K: toggle query editor theme (removed; redundant with app color mode)
  core/frontends/app/src/components/Views/components/useViewBarKeyboardShortcuts.ts (global_document_keydown, own_listener=yes)
    - [x] Cmd+S: save workspace (Primary+KeyS covers both platforms)
    - [x] Cmd+Shift+Z: redo workspace action on macOS
    - [x] Cmd+Z: undo workspace action (Primary+KeyZ covers both platforms)
    - [x] Ctrl+S: save workspace (Primary+KeyS covers both platforms)
    - [x] Ctrl+Y: redo workspace action on non-macOS
    - [x] Ctrl+Z: undo workspace action (Primary+KeyZ covers both platforms)
  core/frontends/app/src/components/WorkspaceDrawer/WorkspaceDrawerContextProvider.tsx (global_window_keydown, own_listener=yes)
    - [x] Escape: close workspace drawer (automatic in cmp lib)
  core/frontends/app/src/util/hotkeys.ts (helper_binding_map, own_listener=no)
    - [x] Alt+V: define non-macOS toggle-visualized-runs binding (entry removed from hotkeys.ts)
    - [ ] Cmd+.: define shared minimize-run-selector binding
    - [ ] Cmd+ArrowLeft: define shared previous-step binding
    - [ ] Cmd+ArrowRight: define shared next-step binding
    - [ ] Cmd+J: define shared runs sidebar toggle binding
    - [x] Cmd+K: define shared app search binding
    - [x] Cmd+S: define shared workspace save binding (entry removed from hotkeys.ts)
    - [x] Cmd+Shift+Z: define macOS workspace redo binding (entry removed from hotkeys.ts)
    - [x] Cmd+Z: define shared workspace undo binding (entry removed from hotkeys.ts)
    - [ ] Ctrl+.: define shared minimize-run-selector binding
    - [ ] Ctrl+ArrowLeft: define shared previous-step binding
    - [ ] Ctrl+ArrowRight: define shared next-step binding
    - [ ] Ctrl+J: define shared runs sidebar toggle binding
    - [x] Ctrl+K: define shared app search binding
    - [x] Ctrl+S: define shared workspace save binding (entry removed from hotkeys.ts)
    - [x] Ctrl+Y: define non-macOS workspace redo binding (entry removed from hotkeys.ts)
    - [x] Ctrl+Z: define shared workspace undo binding (entry removed from hotkeys.ts)
    - [x] Mac Option+V (event.key=√): define macOS toggle-visualized-runs binding
  core/frontends/app/src/util/useColorMode.tsx (global_document_keydown, own_listener=yes)
    - [ ] Alt+M: toggle dark and light color mode
    - [ ] Ctrl+M: toggle dark and light color mode
*/

export const INPUT_ACTION = {
  TOGGLE_APP_THEME: 'toggle-app-theme',
  VIEW_KEYBOARD_SHORTCUTS: 'view-keyboard-shortcuts',
  MINIMIZE_RUN_SELECTOR: 'minimize-run-selector',
  EXPAND_COLLAPSE_RUNS_SIDEBAR: 'expand-collapse-runs-sidebar',
  SAVE_WORKSPACE: 'save-workspace',
  UNDO_WORKSPACE: 'undo-workspace',
  REDO_WORKSPACE: 'redo-workspace',
  TOGGLE_VISUALIZED_RUNS: 'toggle-visualized-runs',
  TOGGLE_APP_SEARCH: 'toggle-app-search',
  MEDIA_MODAL_ZOOM_IN: 'media-modal-zoom-in',
  MEDIA_MODAL_ZOOM_OUT: 'media-modal-zoom-out',
  MEDIA_MODAL_RESET_ZOOM: 'media-modal-reset-zoom',
  MEDIA_MODAL_ZOOM_TO_FIT: 'media-modal-zoom-to-fit',
  WEAVE_CLOSE_PANEL: 'weave-close-panel',
  WEAVE_PANEL_PREVIOUS: 'weave-panel-previous',
  WEAVE_PANEL_NEXT: 'weave-panel-next',
  WEAVE_FOCUS_FILTER: 'weave-focus-filter',
} as const;

export const inputSystem = defineInputSystem({
  global: [
    {
      id: INPUT_ACTION.TOGGLE_APP_THEME,
      name: 'Change app theme',
      shortcut: createShortcut('Alt', 'KeyM'),
    },
    {
      id: INPUT_ACTION.VIEW_KEYBOARD_SHORTCUTS,
      name: 'View keyboard shortcuts',
      shortcut: createShortcut('Primary', 'Slash'),
    },
    {
      id: INPUT_ACTION.TOGGLE_APP_SEARCH,
      name: 'Toggle global search',
      shortcut: createShortcut('Primary', 'KeyK'),
    },
  ],
  workspace: [
    {
      id: INPUT_ACTION.MINIMIZE_RUN_SELECTOR,
      name: 'Toggle minimized run selector',
      description:
        'Minimize or reveal the workspace run selector when the runs table is not expanded.',
      shortcut: createShortcut('Primary', 'Period'),
    },
    {
      id: INPUT_ACTION.EXPAND_COLLAPSE_RUNS_SIDEBAR,
      name: 'Expand or collapse runs sidebar',
      description: 'Expand or collapse the workspace runs sidebar/table.',
      shortcut: createShortcut('Primary', 'KeyJ'),
    },
    {
      id: INPUT_ACTION.SAVE_WORKSPACE,
      name: 'Save workspace',
      description: 'Save pending changes to the current workspace view.',
      shortcut: createShortcut('Primary', 'KeyS'),
    },
    {
      id: INPUT_ACTION.UNDO_WORKSPACE,
      name: 'Undo workspace action',
      description: 'Undo the most recent workspace action.',
      shortcut: createShortcut('Primary', 'KeyZ'),
    },
    {
      id: INPUT_ACTION.REDO_WORKSPACE,
      name: 'Redo workspace action',
      description:
        'Redo the most recently undone workspace action (Cmd/Ctrl+Shift+Z).',
      shortcut: createShortcut('Primary', 'Shift', 'KeyZ'),
    },
    {
      id: INPUT_ACTION.TOGGLE_VISUALIZED_RUNS,
      name: 'Toggle only-show-visualized runs',
      description:
        'Show only the runs that are currently visualized in panels.',
      shortcut: createShortcut('Alt', 'KeyV'),
    },
  ],
  mediaBrowserModal: [
    {
      id: INPUT_ACTION.MEDIA_MODAL_ZOOM_IN,
      name: 'Zoom in',
      description: 'Zoom in on the media in the fullscreen modal.',
      shortcut: createShortcut('Primary', 'Plus'),
    },
    {
      id: INPUT_ACTION.MEDIA_MODAL_ZOOM_OUT,
      name: 'Zoom out',
      description: 'Zoom out on the media in the fullscreen modal.',
      shortcut: createShortcut('Primary', 'Minus'),
    },
    {
      id: INPUT_ACTION.MEDIA_MODAL_RESET_ZOOM,
      name: 'Reset zoom',
      description: 'Reset the fullscreen modal zoom to 100%.',
      shortcut: createShortcut('Primary', 'Digit0'),
    },
    {
      id: INPUT_ACTION.MEDIA_MODAL_ZOOM_TO_FIT,
      name: 'Zoom to fit',
      description: 'Zoom the media to fit the fullscreen modal viewport.',
      shortcut: createShortcut('Shift', 'Digit1'),
    },
  ],
  weave: [
    {
      id: INPUT_ACTION.WEAVE_CLOSE_PANEL,
      name: 'Close side panel',
      description: 'Close the open side panel.',
      shortcut: createShortcut('Escape'),
    },
    {
      id: INPUT_ACTION.WEAVE_PANEL_PREVIOUS,
      name: 'Select previous item',
      description: 'Open the previous item in the list.',
      shortcut: createShortcut('ArrowUp'),
    },
    {
      id: INPUT_ACTION.WEAVE_PANEL_NEXT,
      name: 'Select next item',
      description: 'Open the next item in the list.',
      shortcut: createShortcut('ArrowDown'),
    },
    {
      id: INPUT_ACTION.WEAVE_FOCUS_FILTER,
      name: 'Focus agents filter',
      description: 'Focus the agent filter bar and open its options.',
      shortcut: createShortcut('Slash'),
    },
  ],
});
